import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Check, Minus, Plus, Users, User } from 'lucide-react';

// Backgrounds available per studio
const studioBackgrounds = {
  'solo-studio': [
    { id: 'black', name: 'Professional Black', description: 'Elegant black backdrop for a cinematic look', image: 'https://customer-assets.emergentagent.com/job_73e582ad-7cbe-44ca-9517-11fce7c10b51/artifacts/nw0lkrux_fa2a653a-1fb0-4cbd-ac82-4cc54fee2e27.JPG' },
    { id: 'brown', name: 'Warm Brown', description: 'Warm wooden atmosphere', image: 'https://customer-assets.emergentagent.com/job_73e582ad-7cbe-44ca-9517-11fce7c10b51/artifacts/qs3tnpjq_46b22ac2-5a9b-4961-8002-09489509ed70.JPG' },
  ],
  'podcast-studio': [
    { id: 'corporate', name: 'Elegant Corporate', description: 'Professional and modern style', image: 'https://customer-assets.emergentagent.com/job_73e582ad-7cbe-44ca-9517-11fce7c10b51/artifacts/tzw0973w_022041c2-65bb-4d22-b420-7db17f423558.JPG' },
    { id: 'tropical', name: 'Tropical Bali', description: 'Tropical atmosphere with plants', image: 'https://customer-assets.emergentagent.com/job_73e582ad-7cbe-44ca-9517-11fce7c10b51/artifacts/egv5wnjq_8aab5f74-f98e-4e46-b500-cb31d9afce89.JPG' },
  ]
};

export const MultiStudioSelection = ({ 
  studios, 
  totalHours, 
  studioConfig, 
  onConfigChange 
}) => {
  const [step, setStep] = useState('mode'); // 'mode', 'single-studio', 'single-background', 'multi-hours', 'multi-studio1-bg', 'multi-studio2-bg'
  const [useMultiStudio, setUseMultiStudio] = useState(studioConfig?.useMultiStudio || false);
  
  // Single studio state
  const [singleStudioId, setSingleStudioId] = useState(studioConfig?.singleStudio?.studioId || '');
  const [singleBackgroundId, setSingleBackgroundId] = useState(studioConfig?.singleStudio?.backgroundId || '');
  
  // Multi studio state
  const [studio1, setStudio1] = useState(studioConfig?.multiStudio?.studio1 || { studioId: '', backgroundId: '', hours: Math.floor(totalHours / 2) });
  const [studio2, setStudio2] = useState(studioConfig?.multiStudio?.studio2 || { studioId: '', backgroundId: '', hours: totalHours - Math.floor(totalHours / 2) });

  // Update parent when config changes
  useEffect(() => {
    if (step === 'complete' || (step === 'single-background' && singleBackgroundId)) {
      const config = {
        useMultiStudio,
        singleStudio: !useMultiStudio ? {
          studioId: singleStudioId,
          backgroundId: singleBackgroundId,
          hours: totalHours
        } : null,
        multiStudio: useMultiStudio ? {
          studio1,
          studio2
        } : null
      };
      onConfigChange(config);
    }
  }, [step, useMultiStudio, singleStudioId, singleBackgroundId, studio1, studio2]);

  const handleModeSelect = (multi) => {
    setUseMultiStudio(multi);
    if (multi) {
      // Initialize hours distribution
      const half = Math.floor(totalHours / 2);
      setStudio1(prev => ({ ...prev, hours: half }));
      setStudio2(prev => ({ ...prev, hours: totalHours - half }));
      setStep('multi-hours');
    } else {
      setStep('single-studio');
    }
  };

  const handleSingleStudioSelect = (studioId) => {
    setSingleStudioId(studioId);
    setStep('single-background');
  };

  const handleSingleBackgroundSelect = (backgroundId) => {
    setSingleBackgroundId(backgroundId);
    // Config will be sent to parent via useEffect
  };

  const handleHoursChange = (studio, delta) => {
    const currentS1 = studio1.hours;
    const currentS2 = studio2.hours;
    
    if (studio === 1) {
      const newS1 = Math.max(1, Math.min(totalHours - 1, currentS1 + delta));
      setStudio1(prev => ({ ...prev, hours: newS1 }));
      setStudio2(prev => ({ ...prev, hours: totalHours - newS1 }));
    } else {
      const newS2 = Math.max(1, Math.min(totalHours - 1, currentS2 + delta));
      setStudio2(prev => ({ ...prev, hours: newS2 }));
      setStudio1(prev => ({ ...prev, hours: totalHours - newS2 }));
    }
  };

  const handleMultiStudio1Select = (studioId) => {
    setStudio1(prev => ({ ...prev, studioId }));
    setStep('multi-studio1-bg');
  };

  const handleMultiStudio1BgSelect = (backgroundId) => {
    setStudio1(prev => ({ ...prev, backgroundId }));
    setStep('multi-studio2');
  };

  const handleMultiStudio2Select = (studioId) => {
    setStudio2(prev => ({ ...prev, studioId }));
    setStep('multi-studio2-bg');
  };

  const handleMultiStudio2BgSelect = (backgroundId) => {
    setStudio2(prev => ({ ...prev, backgroundId }));
    setStep('complete');
  };

  const handleBack = () => {
    switch(step) {
      case 'single-studio':
      case 'multi-hours':
        setStep('mode');
        break;
      case 'single-background':
        setStep('single-studio');
        setSingleBackgroundId('');
        break;
      case 'multi-studio1':
        setStep('multi-hours');
        break;
      case 'multi-studio1-bg':
        setStep('multi-studio1');
        setStudio1(prev => ({ ...prev, backgroundId: '' }));
        break;
      case 'multi-studio2':
        setStep('multi-studio1-bg');
        break;
      case 'multi-studio2-bg':
        setStep('multi-studio2');
        setStudio2(prev => ({ ...prev, backgroundId: '' }));
        break;
      default:
        break;
    }
  };

  const getStudioById = (id) => studios.find(s => s.id === id);
  const getBackgrounds = (studioId) => studioBackgrounds[studioId] || [];

  // Render Mode Selection (1 or 2 studios)
  const renderModeSelection = () => (
    <motion.div
      key="mode-selection"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">
        How Many Studios?
      </h2>
      <p className="text-center text-sm text-foreground-muted mb-8">
        You have {totalHours} hours - use 1 or 2 different studios
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* Single Studio Option */}
        <div
          onClick={() => handleModeSelect(false)}
          className="cursor-pointer border-2 border-border hover:border-primary transition-all duration-300 p-8 text-center group"
          data-testid="select-single-studio-mode"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <User className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-heading font-semibold mb-2">1 Studio</h3>
          <p className="text-sm text-foreground-muted">
            {totalHours}h in a single studio with one ambiance
          </p>
        </div>

        {/* Multi Studio Option */}
        <div
          onClick={() => handleModeSelect(true)}
          className="cursor-pointer border-2 border-border hover:border-primary transition-all duration-300 p-8 text-center group"
          data-testid="select-multi-studio-mode"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-heading font-semibold mb-2">2 Studios</h3>
          <p className="text-sm text-foreground-muted">
            Split your {totalHours}h between 2 different studios
          </p>
        </div>
      </div>
    </motion.div>
  );

  // Render Hours Distribution
  const renderHoursDistribution = () => (
    <motion.div
      key="hours-distribution"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-foreground-muted hover:text-foreground mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </button>

      <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">
        Distribute Your Hours
      </h2>
      <p className="text-center text-sm text-foreground-muted mb-8">
        Choose how to split {totalHours}h between 2 studios
      </p>

      <div className="max-w-md mx-auto space-y-6">
        {/* Studio 1 Hours */}
        <div className="bg-muted/30 border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold">Studio 1</span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleHoursChange(1, -1)}
                disabled={studio1.hours <= 1}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio1-hours-minus"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-2xl font-heading font-bold text-primary w-16 text-center">
                {studio1.hours}h
              </span>
              <button
                onClick={() => handleHoursChange(1, 1)}
                disabled={studio1.hours >= totalHours - 1}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio1-hours-plus"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="h-2 bg-background rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(studio1.hours / totalHours) * 100}%` }}
            />
          </div>
        </div>

        {/* Studio 2 Hours */}
        <div className="bg-muted/30 border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold">Studio 2</span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleHoursChange(2, -1)}
                disabled={studio2.hours <= 1}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio2-hours-minus"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-2xl font-heading font-bold text-primary w-16 text-center">
                {studio2.hours}h
              </span>
              <button
                onClick={() => handleHoursChange(2, 1)}
                disabled={studio2.hours >= totalHours - 1}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio2-hours-plus"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="h-2 bg-background rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#9333EA] transition-all duration-300"
              style={{ width: `${(studio2.hours / totalHours) * 100}%` }}
            />
          </div>
        </div>

        {/* Continue Button */}
        <button
          onClick={() => setStep('multi-studio1')}
          className="w-full py-4 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
          data-testid="continue-to-studio1"
        >
          Continue - Choose Studios
        </button>
      </div>
    </motion.div>
  );

  // Render Studio Selection (for both single and multi modes)
  const renderStudioSelection = (forStudio = 'single') => {
    const title = forStudio === 'single' 
      ? 'Choose Your Studio'
      : forStudio === 'studio1'
        ? `Choose Studio 1 (${studio1.hours}h)`
        : `Choose Studio 2 (${studio2.hours}h)`;
    
    const onSelect = forStudio === 'single'
      ? handleSingleStudioSelect
      : forStudio === 'studio1'
        ? handleMultiStudio1Select
        : handleMultiStudio2Select;

    // For multi-studio, disable already selected studio
    const disabledStudioId = forStudio === 'studio2' ? studio1.studioId : null;

    return (
      <motion.div
        key={`studio-selection-${forStudio}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-foreground-muted hover:text-foreground mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">
          {title}
        </h2>
        <p className="text-center text-sm text-foreground-muted mb-8">
          Select the studio that fits your needs
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {studios.map((studio) => {
            const isDisabled = studio.id === disabledStudioId;
            return (
              <div
                key={studio.id}
                onClick={() => !isDisabled && onSelect(studio.id)}
                className={`cursor-pointer border-2 transition-all duration-300 overflow-hidden group ${
                  isDisabled 
                    ? 'opacity-40 cursor-not-allowed border-border' 
                    : 'border-border hover:border-primary'
                }`}
                data-testid={`select-studio-${studio.id}-${forStudio}`}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={studio.image_url}
                    alt={studio.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
                  {isDisabled && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                      <span className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold">
                        Already selected for Studio 1
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold mb-2">{studio.name}</h3>
                  <p className="text-sm text-foreground-muted mb-4">{studio.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-muted">
                      {studioBackgrounds[studio.id]?.length || 0} backgrounds available
                    </span>
                    {!isDisabled && (
                      <span className="text-sm text-primary font-semibold group-hover:translate-x-1 transition-transform">
                        View backgrounds →
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    );
  };

  // Render Background Selection
  const renderBackgroundSelection = (forStudio = 'single') => {
    let studioId, onSelect, title;
    
    if (forStudio === 'single') {
      studioId = singleStudioId;
      onSelect = handleSingleBackgroundSelect;
      title = 'Choose Your Background';
    } else if (forStudio === 'studio1') {
      studioId = studio1.studioId;
      onSelect = handleMultiStudio1BgSelect;
      title = `Choose Background for Studio 1 (${studio1.hours}h)`;
    } else {
      studioId = studio2.studioId;
      onSelect = handleMultiStudio2BgSelect;
      title = `Choose Background for Studio 2 (${studio2.hours}h)`;
    }

    const backgrounds = getBackgrounds(studioId);
    const selectedBg = forStudio === 'single' 
      ? singleBackgroundId 
      : forStudio === 'studio1' 
        ? studio1.backgroundId 
        : studio2.backgroundId;

    return (
      <motion.div
        key={`background-selection-${forStudio}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-foreground-muted hover:text-foreground mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Change studio</span>
        </button>

        <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-2 text-center">
          {title}
        </h2>
        <p className="text-center text-sm text-foreground-muted mb-2">
          {getStudioById(studioId)?.name}
        </p>
        <p className="text-center text-xs text-primary mb-8">
          Click on the background of your choice
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {backgrounds.map((bg) => (
            <div
              key={bg.id}
              onClick={() => onSelect(bg.id)}
              className={`cursor-pointer border-2 transition-all duration-300 overflow-hidden group relative ${
                selectedBg === bg.id
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid={`select-background-${bg.id}-${forStudio}`}
            >
              {selectedBg === bg.id && (
                <div className="absolute top-4 right-4 z-10 bg-primary text-primary-foreground p-2 rounded-full">
                  <Check className="w-4 h-4" />
                </div>
              )}

              <div className="aspect-video overflow-hidden relative">
                <img
                  src={bg.image}
                  alt={bg.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-heading font-semibold mb-2">{bg.name}</h3>
                <p className="text-sm text-foreground-muted">{bg.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 bg-muted/30 border border-border text-center">
          <p className="text-sm text-foreground-muted">
            You can discuss custom adjustments (plants, accessories) on the shooting day
          </p>
        </div>
      </motion.div>
    );
  };

  // Render Summary (for multi-studio complete state)
  const renderSummary = () => (
    <motion.div
      key="summary"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">
        Your Studio Selection
      </h2>
      <p className="text-center text-sm text-foreground-muted mb-8">
        {totalHours} hours split between 2 studios
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Studio 1 Summary */}
        <div className="border-2 border-primary bg-primary/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-primary font-semibold">Studio 1</span>
            <span className="text-lg font-heading font-bold text-primary">{studio1.hours}h</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">{getStudioById(studio1.studioId)?.name}</h3>
          <p className="text-sm text-foreground-muted">
            {getBackgrounds(studio1.studioId).find(b => b.id === studio1.backgroundId)?.name}
          </p>
        </div>

        {/* Studio 2 Summary */}
        <div className="border-2 border-[#9333EA] bg-[#9333EA]/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-[#9333EA] font-semibold">Studio 2</span>
            <span className="text-lg font-heading font-bold text-[#9333EA]">{studio2.hours}h</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">{getStudioById(studio2.studioId)?.name}</h3>
          <p className="text-sm text-foreground-muted">
            {getBackgrounds(studio2.studioId).find(b => b.id === studio2.backgroundId)?.name}
          </p>
        </div>
      </div>

      <button
        onClick={() => setStep('mode')}
        className="w-full py-3 border border-border text-foreground-muted hover:border-primary hover:text-foreground transition-colors"
      >
        Change Selection
      </button>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence mode="wait">
        {step === 'mode' && renderModeSelection()}
        {step === 'single-studio' && renderStudioSelection('single')}
        {step === 'single-background' && renderBackgroundSelection('single')}
        {step === 'multi-hours' && renderHoursDistribution()}
        {step === 'multi-studio1' && renderStudioSelection('studio1')}
        {step === 'multi-studio1-bg' && renderBackgroundSelection('studio1')}
        {step === 'multi-studio2' && renderStudioSelection('studio2')}
        {step === 'multi-studio2-bg' && renderBackgroundSelection('studio2')}
        {step === 'complete' && renderSummary()}
      </AnimatePresence>
    </motion.div>
  );
};
